import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { ModelEditor } from 'src/app/shared/editors/modeleditor';
import { AppealDetailResponse, AppealMediaResponse, CitationDetailResponse, EnforcementService, VoidReasonResponse, AppealDecisionRequest, } from 'src/app/Services/enforcement.service';
import { MediaService } from 'src/app/Services/media.service';
import { AppealActionHistoryModalComponent } from './action-history-modal/action-history-modal.component';
import { AddNoteModalComponent } from 'src/app/shared/modals/add-note-modal/add-note-modal.component';
import { FormBuilder, Validators, AbstractControl, ValidationErrors, FormGroup, FormControl, ValidatorFn, } from '@angular/forms';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-appeal',
    templateUrl: './appeal.component.html',
    styleUrls: ['./appeal.component.scss'],
    standalone: false
})
export class AppealComponent extends ModelEditor implements OnInit {
  declare public Model: AppealDetailResponse;
  public Citation?: CitationDetailResponse;
  public citationLoading = false;

  public communicationMessage = '';
  public busy = false;
  public communicationBusy = false;
  public noteBusy = false;


  // Decision form
  public voidReasons: VoidReasonResponse[] = [];

  public mediaPreviews: {
    [mediaId: number]: {
      dataUrl: string;
      isImage: boolean;
      name?: string;
      extension?: string;
    };
  } = {};
  public mediaImageErrors: { [mediaId: number]: boolean } = {};

  constructor(
    public route: ActivatedRoute,
    public enforcementService: EnforcementService,
    private mediaService: MediaService,
    private injector: Injector,
    private toastr: ToastrService,
    private modalService: SimpleModalService,
    private formBuilder: FormBuilder,
  ) {
    super('enforcement/appeals', injector);
  }

  public DefaultModel() {
    return {
      Id: 0,
      CitationId: 0,
      InfringementId: 0,
      CitationReference: '',
      InfringementTypeName: null,
      SubmissionDate: '',
      Status: '',
      EmailAddress: '',
      ReasonText: '',
      Decision: 0,
      DecisionName: null,
      IsUpheld: false,
      IsDenied: false,
      DecisionText: '',
      DecisionDate: null,
      DecisionByUserId: null,
      DecisionByUserName: null,
      CurrentStateKey: '',
      StateEnteredDate: '',
      SubmissionDateLocalFriendlyString: '',
      DecisionDateLocalFriendlyString: null,
      MediaCount: 0,
      CommunicationCount: 0,
      Communications: [],
      Media: [],
      DecisionMedia: [],
      Notes: [],
    } as AppealDetailResponse;
  }

  public AfterModelLoaded(): void {
    this.loadCitationSummary();
    this.preloadMediaPreviews();
    this.loadVoidReasons();
  }

  private async loadVoidReasons() {
    try {
      this.voidReasons = await this.enforcementService.getVoidReasons();
    } catch (error) {
      console.error('Error loading void reasons:', error);
      this.voidReasons = [];
    }
  }

  private async loadCitationSummary() {
    if (!this.Model || !this.Model.CitationId) {
      this.Citation = undefined;
      return;
    }

    this.citationLoading = true;
    try {
      this.Citation = await this.enforcementService.getCitation(
        this.Model.CitationId,
      );
      const adjustmentAmountControl =
        this.decisionForm.controls.adjustmentAmount;

      const infringmentPrice =
        this.Citation.Infringements.find(
          (x) => x.Id === this.Model.InfringementId,
        )?.CurrentPrice ?? 0;

      adjustmentAmountControl.setValidators([this.validateAdjustmentAmount(infringmentPrice, this.Citation.TotalOutstanding, this.Citation.TotalPaid)]);

      adjustmentAmountControl.updateValueAndValidity({ emitEvent: false });
    } catch (error) {
      console.error('Failed to load citation summary for appeal', error);
      this.Citation = undefined;
    } finally {
      this.citationLoading = false;
    }
  }

  public BeforeSave(): boolean | void { }

  public Validators() {
    return {};
  }

  public formatStatus(status?: string | null): string {
    if (!status) {
      return '';
    }
    return status
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, (letter) => letter.toUpperCase());
  }

  // start of decision code
  initialDecisionFormValues: any;
  override ngOnInit(): void {
    super.ngOnInit();

    this.decisionForm = this.formBuilder.group({
      adjustmentAmount: [null as number | null],
      adjustmentReason: ['', [this.validateAdjustmentReason]],
      isUpheld: [null as boolean | null, [Validators.required]],
      withAdjustments: [null as boolean | null, [this.validateWithAdjustments]],
      decisionNotes: ['', [Validators.required, Validators.maxLength(4000), this.nonEmptyTrimmed]],
      selectedVoidReasonId: [null as number | null, [this.validateSelectedVoidReasonId]],
      freezeAfterAdjustment: [false],
      freezeReason: ['', [this.validateFreezeReason]],
      freezeIndefinitely: [true],
      freezeUntil: [null as Date | null, [this.validateFreezeUntil]],
    });

    this.initialDecisionFormValues = this.decisionForm.getRawValue();

    this.setupConditionalRevalidation();
  }

  private readonly subs = new Subscription();

  private setupConditionalRevalidation(): void {
    const revalidate = (controlNames: string[]) => {
      controlNames.forEach((name) => {
        this.decisionForm
          .get(name)!
          .updateValueAndValidity({ emitEvent: false });
      });
    };

    this.subs.add(
      this.decisionForm.controls.isUpheld.valueChanges.subscribe(() => {
        revalidate([
          'adjustmentAmount',
          'adjustmentReason',
          'selectedVoidReasonId',
          'adjustmentReason',
          'freezeReason',
          'freezeUntil',
          'withAdjustments',
        ]);
      })
    );

    this.subs.add(
      this.decisionForm.controls.withAdjustments.valueChanges.subscribe(() => {
        revalidate([
          'adjustmentAmount',
          'adjustmentReason',
          'selectedVoidReasonId',
          'adjustmentReason',
          'freezeReason',
          'freezeUntil',
        ]);
      })
    );

    this.subs.add(
      this.decisionForm.controls.freezeAfterAdjustment.valueChanges.subscribe(
        () => {
          revalidate(['freezeReason', 'freezeUntil']);
        },
      )
    );

    this.subs.add(
      this.decisionForm.controls.freezeIndefinitely.valueChanges.subscribe(() => {
        revalidate(['freezeUntil']);
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  decisionForm!: FormGroup<{
    adjustmentAmount: FormControl<number | null>;
    adjustmentReason: FormControl<string | null>;
    isUpheld: FormControl<boolean | null>;
    withAdjustments: FormControl<boolean | null>;
    decisionNotes: FormControl<string | null>;
    selectedVoidReasonId: FormControl<number | null>;
    freezeAfterAdjustment: FormControl<boolean | null>;
    freezeReason: FormControl<string | null>;
    freezeIndefinitely: FormControl<boolean | null>;
    freezeUntil: FormControl<Date | null>;
  }>;

  validateWithAdjustments(control: AbstractControl): ValidationErrors | null {
    const group = control.parent as FormGroup | null;
    const isUpheld = group?.get('isUpheld')?.value as boolean;
    if (!isUpheld) {
      return null;
    }
    const withAdjustments = control.value;
    if (withAdjustments === null) {
      return {
        withAdjustments: 'Please select an action.',
      };
    }
    return null;
  }

  validateAdjustmentAmount(InfringmentPrice: number, TotalOutstanding: number, TotalPaid: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control.parent as FormGroup | null;
      const isUpheld = group?.get('isUpheld')?.value as boolean;
      const withAdjustments = group?.get('withAdjustments')?.value as boolean;
      if (!isUpheld || !withAdjustments) {
        return null;
      }
      const amount = control.value;
      if (amount === null) {
        return { adjustmentAmount: 'The new infringement fee is required.' };
      }

      if (amount < 0) {
        return { adjustmentAmount: 'The new fee cannot be negative.' };
      }

      if (amount >= InfringmentPrice) {
        return {
          adjustmentAmount: `The new fee must be less than the current fee of $${InfringmentPrice}.`,
        };
      }

      var discount = InfringmentPrice - amount;
      if (discount > TotalOutstanding + TotalPaid) {
        const minimumAllowedFee =
          InfringmentPrice - TotalOutstanding - TotalPaid;
        return {
          adjustmentAmount: `The fee cannot be reduced to $${amount} because existing discounts on this citation would make the total negative. The minimum allowed fee is $${minimumAllowedFee}.`,
        };
      }

      return null;
    };
  }

  nonEmptyTrimmed(c: AbstractControl): ValidationErrors | null {
    if (typeof c.value === 'string' && c.value.trim() === '') {
      return { required: true };
    } else {
      return null;
    }
  }

  validateAdjustmentReason(control: AbstractControl): ValidationErrors | null {
    const group = control.parent as FormGroup | null;
    const isUpheld = group?.get('isUpheld')?.value as boolean;
    const withAdjustments = group?.get('withAdjustments')?.value as boolean;
    if (!isUpheld || !withAdjustments) {
      return null;
    }
    const reason = control.value;
    if (reason === null || reason.trim() === '') {
      return { adjustmentReason: 'A reason for the fee reduction is required.' };
    }
    if (reason.length > 512) {
      return {
        adjustmentReason: 'Reason cannot exceed 512 characters.',
      };
    }
    return null;
  }

  validateFreezeUntil(control: AbstractControl): ValidationErrors | null {
    const group = control.parent as FormGroup | null;
    const isUpheld = group?.get('isUpheld')?.value as boolean;
    const withAdjustments = group?.get('withAdjustments')?.value as boolean;
    const freezeAfterAdjustment = group?.get('freezeAfterAdjustment')?.value as boolean;
    const freezeIndefinitely = group?.get('freezeIndefinitely')?.value as boolean;
    if (
      !isUpheld ||
      !withAdjustments ||
      !freezeAfterAdjustment ||
      freezeIndefinitely
    ) {
      return null;
    }
    const until = control.value;
    if (until === null) {
      return { freezeUntil: 'A freeze end date is required.' };
    }
    const untilDate = new Date(until);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (untilDate <= today) {
      return { freezeUntil: 'The freeze end date must be in the future.' };
    }
    return null;
  }

  public validateFreezeReason(control: AbstractControl): ValidationErrors | null {
    const group = control.parent as FormGroup | null;
    const isUpheld = group?.get('isUpheld')?.value as boolean;
    const withAdjustments = group?.get('withAdjustments')?.value as boolean;
    const freezeAfterAdjustment = group?.get('freezeAfterAdjustment')?.value as boolean;
    if (!isUpheld || !withAdjustments || !freezeAfterAdjustment) {
      return null;
    }
    const reason = control.value;
    if (reason === null || reason.trim() === '') {
      return { freezeReason: 'A reason for the freeze is required.' };
    }
    if (reason.length > 512) {
      return {
        freezeReason: 'Reason cannot exceed 512 characters.',
      };
    }
    return null;
  }

  public validateSelectedVoidReasonId(control: AbstractControl): ValidationErrors | null {
    const group = control.parent as FormGroup | null;
    const isUpheld = group?.get('isUpheld')?.value as boolean;
    const withAdjustments = group?.get('withAdjustments')?.value as boolean;
    if (!isUpheld || withAdjustments) {
      return null;
    }
    if (control.value === null) {
      return { selectedVoidReasonId: 'Please select a void reason.' };
    }
    return null;
  }

  public async submitDecision() {
    if (
      this.decisionForm.invalid ||
      this.Citation === null ||
      this.Citation === undefined
    ) {
      this.decisionForm.markAllAsTouched();
      return;
    }

    if (this.uploadingFile || this.pendingUploadCount > 0) {
      this.toastr.warning('Please wait for all evidence files to finish uploading.', 'Appeals');
      return;
    }

    this.busy = true;
    try {
      const infringement = this.Citation.Infringements.find(
        (x) => x.Id === this.Model.InfringementId,
      );
      if (infringement === null || infringement === undefined) {
        return;
      }
      const {
        adjustmentAmount,
        adjustmentReason,
        isUpheld,
        withAdjustments,
        decisionNotes,
        selectedVoidReasonId,
        freezeAfterAdjustment,
        freezeReason,
        freezeIndefinitely,
        freezeUntil,
      } = this.decisionForm.getRawValue();

      const reqeast: AppealDecisionRequest = {
        // 1 = Upheld, 2 = Denied, 3 = Upheld with adjustments (matches AppealDecision enum)
        Decision: isUpheld ? (withAdjustments ? 3 : 1) : 2,
        DecisionText: decisionNotes!,
        MediaIds: this.uploadedMediaIds
      };
      if (isUpheld && !withAdjustments) {
        reqeast.VoidReasonId = selectedVoidReasonId;
      } else if (isUpheld && withAdjustments) {
        if (adjustmentAmount === null) {
          return;
        }
        reqeast.AdjustmentAmount = infringement.CurrentPrice - adjustmentAmount;
        reqeast.AdjustmentReason = adjustmentReason;
        reqeast.FreezeAfterAdjustment = freezeAfterAdjustment;
        if (freezeAfterAdjustment) {
          reqeast.FreezeReason = freezeReason;
          reqeast.FreezeIndefinitely = freezeIndefinitely;
          reqeast.FreezeUntil = freezeUntil;
        }
      }

      await this.enforcementService.decideAppeal(this.Model.Id, reqeast);
      this.clearDecisionUploadState();
      this.FetchModel(this.ModelId);
      this.toastr.success(`Appeal ${isUpheld ? 'Upheld' : 'Denied'}`, 'Appeals');
    } catch (error: any) {
      const errorMsg = error?.error?.error || 'Unable to update appeal decision';
      this.toastr.error(errorMsg, 'Appeals');
      console.error(error);
    } finally {
      this.busy = false;
    }
  }
  // End of Decision Code

  public async updateState(stateKey: string) {
    if (!this.Model) {
      return;
    }
    this.busy = true;
    try {
      await this.enforcementService.updateAppealStatus(this.Model.Id, {
        StateKey: stateKey,
      });
      this.FetchModel(this.ModelId);
      this.toastr.success('Appeal status updated', 'Appeals');
    } catch (error) {
      this.toastr.error('Unable to update appeal status', 'Appeals');
      console.error(error);
    } finally {
      this.busy = false;
    }
  }

  public async undoDecision() {
    if (!this.Model) {
      return;
    }
    this.busy = true;
    try {
      // Clear the decision by calling the undo endpoint
      await this.enforcementService.undoAppealDecision(this.Model.Id);
      // Reset the decision form
      this.decisionForm.reset(this.initialDecisionFormValues);
      this.clearDecisionUploadState();

      this.FetchModel(this.ModelId);
      this.toastr.success('Decision undone - appeal returned to review', 'Appeals',);
    } catch (error: any) {
      const errorMsg = error?.error?.error || 'Unable to undo decision';
      this.toastr.error(errorMsg, 'Appeals');
      console.error(error);
    } finally {
      this.busy = false;
    }
  }

  public async addCommunication() {
    if (!this.communicationMessage || !this.Model) {
      return;
    }
    this.communicationBusy = true;
    try {
      await this.enforcementService.addAppealCommunication(this.Model.Id, {
        Message: this.communicationMessage,
      });
      this.communicationMessage = '';
      this.FetchModel(this.ModelId);
    } catch (error) {
      this.toastr.error('Unable to add communication', 'Appeals');
      console.error(error);
    } finally {
      this.communicationBusy = false;
    }
  }

  public refreshAppeal() {
    this.FetchModel(this.ModelId);
  }

  public async viewHistory() {
    if (!this.Model) {
      return;
    }
    try {
      const history = await this.enforcementService.getAppealActionHistory(this.Model.Id);
      this.modalService.addModal(AppealActionHistoryModalComponent, {
        actions: history,
      });
    } catch (error) {
      this.toastr.error('Unable to load action history', 'Appeals');
      console.error(error);
    }
  }

  private preloadMediaPreviews(): void {
    if (!this.Model?.Media?.length && !this.Model?.DecisionMedia?.length) {
      return;
    }

    this.Model.Media.forEach(media => this.ensureMediaPreview(media));
    this.Model.DecisionMedia.forEach(media => this.ensureMediaPreview(media));
  }

  private ensureMediaPreview(media: AppealMediaResponse): void {
    if (!media || !media.MediaId || this.mediaPreviews[media.MediaId]) {
      return;
    }

    this.mediaService
      .GetBase64Media(media.MediaId.toString())
      .subscribe((response) => {
        if (!response || !response.Base64String) {
          return;
        }

        const prefix: string =
          response.Base64Prefix || 'data:application/octet-stream;base64,';
        const dataUrl = `${prefix}${response.Base64String}`;
        const isImage = prefix.startsWith('data:image');
        const extension = (response.Extension || '').replace(/^\./, '');

        this.mediaPreviews[media.MediaId] = {
          dataUrl,
          isImage,
          name: response.Name,
          extension,
        };
      });
  }

  public openMedia(media: AppealMediaResponse): void {
    if (!media || !media.MediaId) {
      return;
    }

    const existing = this.mediaPreviews[media.MediaId];
    if (existing) {
      this.openFromPreview(
        existing,
        existing.name || `Media #${media.MediaId}`,
      );
      return;
    }

    this.mediaService.GetBase64Media(media.MediaId.toString()).subscribe(
      (response) => {
        if (!response || !response.Base64String) {
          this.toastr.error('Unable to load media', 'Appeals');
          return;
        }

        const prefix: string =
          response.Base64Prefix || 'data:application/octet-stream;base64,';
        const dataUrl = `${prefix}${response.Base64String}`;
        const isImage = prefix.startsWith('data:image');
        const extension = (response.Extension || '').replace(/^\./, '');
        const preview = { dataUrl, isImage, name: response.Name, extension };

        this.mediaPreviews[media.MediaId] = preview;
        this.openFromPreview(
          preview,
          preview.name || `Media #${media.MediaId}`,
        );
      },
      () => {
        this.toastr.error('Unable to load media', 'Appeals');
      },
    );
  }

  private openFromPreview(
    preview: { dataUrl: string; isImage: boolean; name?: string },
    name: string,
  ): void {
    try {
      const link = document.createElement('a');
      link.href = preview.dataUrl;
      link.download = name || 'appeal-media';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      this.toastr.error('Unable to download media', 'Appeals');
    }
  }

  public getMediaIconClass(mediaId: number): string {
    // Use a generic document icon (Font Awesome "file-o" equivalent)
    return 'far fa-file';
  }

  public onMediaImageError(mediaId: number): void {
    this.mediaImageErrors[mediaId] = true;
  }

  public addNote(): void {
    if (!this.Model) {
      return;
    }

    const modalRef = this.modalService.addModal(AddNoteModalComponent, {
      entityType: 'appeal',
      entityId: this.Model.Id,
      entityReference: this.Model.Id.toString(),
    });

    modalRef.subscribe(async (result) => {
      if (result && this.Model) {
        try {
          this.noteBusy = true;
          const note = await this.enforcementService.addAppealNote(this.Model.Id, { Content: result.content });
          // Add the new note to the beginning of the array (newest first)
          this.Model = {
            ...this.Model,
            Notes: [note, ...(this.Model.Notes || [])],
          };
        } catch (error) {
          this.toastr.error('Unable to add note', 'Appeals');
          console.error('Error adding note:', error);
        } finally {
          this.noteBusy = false;
        }
      }
    });
  }

  // File upload
  public uploadedMediaIds: number[] = [];
  public isDragging = false;
  public uploadingFile = false;


  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.uploadFiles(Array.from(input.files));
    }
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      this.uploadFiles(Array.from(event.dataTransfer.files));
    }
  }

  private pendingUploadCount = 0;
  private uploadFiles(files: File[]): void {
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        this.uploadingFile = true;
        this.pendingUploadCount++;
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = (e.target?.result as string).split(',')[1];
          this.mediaService.UploadMedia(base64, file.name, 'citation-evidence', false).subscribe({
            next: (mediaId) => {
              this.uploadedMediaIds.push(mediaId);
              this.pendingUploadCount--;
              this.uploadingFile = this.pendingUploadCount > 0;
              this.toastr.success(`${file.name} uploaded`, 'Success');
            },
            error: (err) => {
              this.pendingUploadCount--;
              this.uploadingFile = this.pendingUploadCount > 0;
              this.toastr.error(`Failed to upload ${file.name}`, 'Error');
            }
          });
        };
        reader.readAsDataURL(file);
      } else {
        this.toastr.warning(`${file.name} is not an image file`, 'Warning');
      }
    });
  }

  public removeMedia(index: number): void {
    this.uploadedMediaIds.splice(index, 1);
  }

  private clearDecisionUploadState(): void {
    this.uploadedMediaIds = [];
    this.pendingUploadCount = 0;
    this.uploadingFile = false;
    this.isDragging = false;
  }




}
